/* @jsx h */
import { defineComponent, h } from "vue";
import type { CreateCustomerAddressData } from "@lemmonhq/types";
import { useLocalization } from "@lemmonhq/uikit";

export interface CustomerAddressContactFormProps {
  form: CreateCustomerAddressData;
  onUpdate: (field: keyof CreateCustomerAddressData, value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  onEditDetails: () => void;
  isSubmitting: boolean;
  errorMessage: string;
  selectionSummary: {
    label: string;
    street: string;
    postal_code: string;
    city: string;
  } | null;
}

export const CustomerAddressContactForm =
  defineComponent<CustomerAddressContactFormProps>({
    name: "CustomerAddressContactForm",
    props: [
      "form",
      "onUpdate",
      "onSubmit",
      "onCancel",
      "onEditDetails",
      "isSubmitting",
      "errorMessage",
      "selectionSummary",
    ],
    setup(props) {
      const { t } = useLocalization();

      const handleInput = (
        field: keyof CreateCustomerAddressData,
        event: Event,
      ) => {
        props.onUpdate(field, (event.target as HTMLInputElement).value);
      };

      return () => (
        <div class="space-y-5">
          {props.selectionSummary && (
            <div class="rounded-2xl border border-base-300 bg-base-100 p-4 shadow-sm">
              <div class="flex items-start justify-between gap-4">
                <div class="space-y-1">
                  <p class="text-sm uppercase tracking-wide text-base-content/50">
                    {t("pos.phone_order.selected_address")}
                  </p>
                  <h4 class="text-base font-semibold leading-snug">
                    {props.selectionSummary.label}
                  </h4>
                  <p class="text-sm text-base-content/70 leading-snug">
                    {props.selectionSummary.street}
                    <br />
                    {props.selectionSummary.postal_code}{" "}
                    {props.selectionSummary.city}
                  </p>
                </div>
                <button
                  class="btn btn-ghost btn-sm"
                  onClick={props.onEditDetails}
                >
                  {t("pos.phone_order.edit_details")}
                </button>
              </div>
            </div>
          )}

          {props.errorMessage && (
            <div class="alert alert-error">
              <span class="material-symbols-outlined">error</span>
              <div>
                <p class="font-semibold">{props.errorMessage}</p>
                {props.errorMessage.includes("street") ||
                props.errorMessage.includes("postal") ||
                props.errorMessage.includes("city") ? (
                  <p class="text-sm mt-1 opacity-90">
                    {t("pos.phone_order.address_error_hint")}
                  </p>
                ) : null}
              </div>
            </div>
          )}

          <div class="space-y-4">
            <div class="grid gap-4 sm:grid-cols-2">
              <label class="form-control">
                <span class="label-text font-medium">
                  {t("general.labels.first_name")} *
                </span>
                <input
                  type="text"
                  class="input input-bordered"
                  placeholder={t("general.placeholders.first_name_sample")}
                  value={props.form.first_name}
                  onInput={(event) => handleInput("first_name", event)}
                />
              </label>
              <label class="form-control">
                <span class="label-text font-medium">
                  {t("general.labels.last_name")} *
                </span>
                <input
                  type="text"
                  class="input input-bordered"
                  placeholder={t("general.placeholders.last_name_sample")}
                  value={props.form.last_name}
                  onInput={(event) => handleInput("last_name", event)}
                />
              </label>
            </div>

            <div class="grid gap-4 sm:grid-cols-2">
              <label class="form-control">
                <span class="label-text font-medium">
                  {t("general.labels.email")} *
                </span>
                <input
                  type="email"
                  class="input input-bordered"
                  placeholder={t("general.placeholders.email_sample")}
                  value={props.form.email}
                  onInput={(event) => handleInput("email", event)}
                />
              </label>
              <label class="form-control">
                <span class="label-text font-medium">
                  {t("general.labels.phone")} *
                </span>
                <input
                  type="tel"
                  class="input input-bordered"
                  placeholder="+41 xxx"
                  value={props.form.phone}
                  onInput={(event) => handleInput("phone", event)}
                />
              </label>
            </div>

            <label class="form-control">
              <span class="label-text font-medium">
                {t("pos.phone_order.delivery_notes")}
              </span>
              <textarea
                class="textarea textarea-bordered"
                rows={3}
                placeholder={t("pos.phone_order.delivery_notes_placeholder")}
                value={props.form.notes || ""}
                onInput={(event) =>
                  props.onUpdate(
                    "notes",
                    (event.target as HTMLTextAreaElement).value,
                  )
                }
              />
            </label>
          </div>

          <div class="flex flex-col gap-3 sm:flex-row sm:justify-end sm:items-center">
            <button
              class="btn btn-ghost w-full sm:w-auto"
              type="button"
              onClick={props.onCancel}
              disabled={props.isSubmitting}
            >
              {t("general.words.cancel")}
            </button>
            <button
              class={`btn btn-primary w-full sm:w-auto ${
                props.isSubmitting ? "" : ""
              }`}
              type="button"
              onClick={props.onSubmit}
              disabled={props.isSubmitting}
            >
              {props.isSubmitting && (
                <span class="loading loading-spinner loading-sm" />
              )}
              {t("pos.phone_order.save_address")}
            </button>
          </div>
        </div>
      );
    },
  });
